import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router';
import { Clapperboard, Home, Tag, Tv, User, Video } from 'lucide-react';
import { getEntitlement } from '../api/rttsClient';
import type { Entitlement } from '../api/types';
import { STUDIO_COPY } from '../config';
import { useStudioAuth } from '../hooks/useStudioAuth';
import { useStudioReelsBadge } from '../hooks/useStudioReelsBadge';
import { STUDIO_LANDING_PATH, studioLscHomeUrl } from '../studioLinks';
import { useStudioHomePath } from '../hooks/useStudioHomePath';
import { formatVodsEntitlementIndicator } from '../utils/vodEligibility';
import studioMark from '../assets/scoutlytics-studio-mark.svg';
import StudioAvatarMenu from './StudioAvatarMenu';
import StudioFeedbackButton from './StudioFeedbackButton';
import StudioHostLink from './StudioHostLink';

function railItemClass(isActive: boolean): string {
  return ['studio-rail-item', isActive ? 'is-active' : ''].filter(Boolean).join(' ');
}

const StudioRail: React.FC = () => {
  const { status, isLoggedOut } = useStudioAuth();
  const { inProgressCount } = useStudioReelsBadge();
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);

  // Entitlement for the rail plan chip — session-scoped, not per pathname.
  useEffect(() => {
    if (isLoggedOut || status !== 'linked') {
      setEntitlement(null);
      return;
    }
    let cancelled = false;
    void getEntitlement()
      .then((next) => {
        if (!cancelled) setEntitlement(next);
      })
      .catch(() => {
        if (!cancelled) setEntitlement(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isLoggedOut, status]);

  const plan = entitlement ? formatVodsEntitlementIndicator(entitlement) : null;
  const lscHref = studioLscHomeUrl();
  const homePath = useStudioHomePath();
  const showAuthed = !isLoggedOut && status !== 'loading';

  return (
    <nav className="studio-rail" aria-label="Studio" data-testid="studio-rail">
      <NavLink
        to={STUDIO_LANDING_PATH}
        className="studio-rail-brand no-underline"
        data-testid="studio-brand"
      >
        <img
          src={studioMark}
          alt="Scoutlytics Studio"
          className="studio-rail-brand-img"
          data-testid="studio-brand-logo"
        />
      </NavLink>

      {plan ? (
        <p className="studio-rail-plan" data-testid="studio-rail-plan">
          {plan.text}
        </p>
      ) : null}

      <div className="studio-rail-primary">
        <NavLink
          to={homePath}
          end
          className={({ isActive }) => railItemClass(isActive)}
          data-testid="studio-rail-home"
        >
          <span className="studio-rail-icon" aria-hidden>
            <Home size={20} strokeWidth={1.75} />
          </span>
          <span className="studio-rail-label">{STUDIO_COPY.railHomeLabel}</span>
        </NavLink>
        <NavLink
          to="/studio/vods"
          className={({ isActive }) => railItemClass(isActive)}
          data-testid="studio-rail-vods"
        >
          <span className="studio-rail-icon" aria-hidden>
            <Video size={20} strokeWidth={1.75} />
          </span>
          <span className="studio-rail-label">{STUDIO_COPY.railVodsLabel}</span>
        </NavLink>
        <NavLink
          to="/studio/reels"
          className={({ isActive }) =>
            `${railItemClass(isActive)} studio-nav-reels`
          }
          data-testid="studio-rail-reels"
        >
          <span className="studio-rail-icon" aria-hidden>
            <Clapperboard size={20} strokeWidth={1.75} />
          </span>
          <span className="studio-rail-label">{STUDIO_COPY.railReelsLabel}</span>
          {inProgressCount > 0 ? (
            <span
              className="studio-nav-reels-badge"
              data-testid="studio-nav-reels-badge"
              aria-label={`${inProgressCount} reels in progress`}
            >
              {inProgressCount > 9 ? '9+' : inProgressCount}
            </span>
          ) : null}
        </NavLink>
      </div>

      <div className="studio-rail-divider" aria-hidden />

      <div className="studio-rail-secondary">
        <StudioHostLink
          href={lscHref}
          className="studio-rail-item no-underline"
          testId="studio-rail-lsc"
        >
          <span className="studio-rail-icon" aria-hidden>
            <Tv size={20} strokeWidth={1.75} />
          </span>
          <span className="studio-rail-label">{STUDIO_COPY.railLscLabel}</span>
        </StudioHostLink>
        <NavLink
          to="/studio/upgrade"
          end
          className={({ isActive }) => railItemClass(isActive)}
          data-testid="studio-rail-plans"
        >
          <span className="studio-rail-icon" aria-hidden>
            <Tag size={20} strokeWidth={1.75} />
          </span>
          <span className="studio-rail-label">{STUDIO_COPY.railPlansLabel}</span>
        </NavLink>
        <NavLink
          to="/studio/account"
          className={({ isActive }) => railItemClass(isActive)}
          data-testid="studio-rail-account"
        >
          <span className="studio-rail-icon" aria-hidden>
            <User size={20} strokeWidth={1.75} />
          </span>
          <span className="studio-rail-label">{STUDIO_COPY.railAccountLabel}</span>
        </NavLink>
      </div>

      <div className="studio-rail-lower">
        {showAuthed ? <StudioFeedbackButton variant="rail" /> : null}
        <StudioAvatarMenu />
      </div>
    </nav>
  );
};

export default StudioRail;
